import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Modal } from '@unified-checkout/design-system';
import { Xmark } from '@unified-checkout/design-system/icons/index';
import type { IButton } from 'libs/design-system/src/types/Button/button';
import type { IDigest } from '@app/dashboard/types/innerLayout/services/digest';
import { environment } from '@app/dashboard/environments/environment';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { MAX_NOTIFICATION_WIDTH, NOTIFICATION_TIMEOUT } from '@app/dashboard/constants/transactions';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { changeSubscription } from '@app/dashboard/services/inner_layout/services/change-subscription';
import { getServices } from '@app/dashboard/services/inner_layout/services/get-services';
import { addToLocalStorage, getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { notify } from '@app/dashboard/utils/notifier';

export const DigestModal = ({
    isDigestOpen,
    setisDigestOpen,
    setIsDigestEnabled,
    isDigestEnabled
}: IDigest) => {
    const { t } = useTranslation();

    const digestId = getFromLocalStorage(LOCAL_STORAGE_KEYS.DIGEST)?.id;

    useEffect(() => {
        if (isDigestEnabled === null || (isDigestEnabled && digestId === null)) {
            mutateServices();
        }
    }, [isDigestEnabled]);

    const {
        mutate: mutateServices,
    } = useMutation({
        mutationKey: [QUERY_KEYS.Get_SERVICES],
        mutationFn: () => getServices(),
        onSuccess: (resp) => {
            if (resp?.data?.results && resp.data.results.length > 0) {
                const digestService = resp.data.results.find((service: any) =>
                    service.service_name === environment.DIGEST_SERVICE_NAME
                );
                if (digestService) {
                    addToLocalStorage(LOCAL_STORAGE_KEYS.DIGEST, {
                        enabled: digestService.active,
                        id: digestService.service_id
                    });
                } else {
                    addToLocalStorage(LOCAL_STORAGE_KEYS.DIGEST, null);
                }
            } else {
                addToLocalStorage(LOCAL_STORAGE_KEYS.DIGEST, null);
            }
        },
        onError: () => {
            addToLocalStorage(LOCAL_STORAGE_KEYS.DIGEST, null);
        }
    });

    const {
        mutate: mutateChangeSubscription,
        isPending,
    } = useMutation({
        mutationKey: [QUERY_KEYS.CHANGE_SUBSCRIPTION],
        mutationFn: (isSubscribeAction: boolean) => {
            return changeSubscription(digestId, isSubscribeAction);
        },
        onSuccess: (_, isSubscribeAction) => {
            addToLocalStorage(LOCAL_STORAGE_KEYS.DIGEST, {
                enabled: isSubscribeAction,
                id: digestId
            });
            setIsDigestEnabled(isSubscribeAction);
            const successMessage = isSubscribeAction ?
                t('innerLayout.servicesPage.modals.digest.subscribeSuccess')
                :
                t('innerLayout.servicesPage.modals.digest.unsubscribeSuccess');
            notify.success(successMessage, { maxWidth: MAX_NOTIFICATION_WIDTH, timeout: NOTIFICATION_TIMEOUT });
            setisDigestOpen(false);
        },
        onError: (error: any) => {
            const errMessage = error.response?.data?.message ?? t('innerLayout.servicesPage.modals.digest.failed');
            notify.error(errMessage, { maxWidth: MAX_NOTIFICATION_WIDTH, timeout: NOTIFICATION_TIMEOUT, icon: Xmark });
            setisDigestOpen(false);
        }
    })

    const handleToggleSubscription = (isSubscribeAction: boolean) => {
        mutateChangeSubscription(isSubscribeAction);
    };

    const cancelBtnProps: IButton = {
        text: t('innerLayout.servicesPage.modals.btns.cancel'),
        size: "lg",
        variant: "outlined",
        color: "secondary",
        disabled: isPending,
        onClickHandler: () => setisDigestOpen(false)
    };

    const actionButton: IButton = isDigestEnabled ?
        {
            text: t('innerLayout.servicesPage.modals.btns.unsubscrbe'),
            size: "lg",
            variant: "contained",
            color: "danger",
            loading: isPending,
            disabled: isPending,
            onClickHandler: () => handleToggleSubscription(false)
        }
        :
        {
            text: t('innerLayout.servicesPage.modals.btns.subscribe'),
            size: "lg",
            variant: "contained",
            color: "primary",
            loading: isPending,
            disabled: isPending,
            onClickHandler: () => handleToggleSubscription(true)
        };

    const title = isDigestEnabled ?
        t('innerLayout.servicesPage.modals.digest.unsubscribeTitle')
        :
        t('innerLayout.servicesPage.modals.digest.subscribeTitle');

    const description = isDigestEnabled ?
        t('innerLayout.servicesPage.modals.digest.unsubscribeDesc')
        :
        t('innerLayout.servicesPage.modals.digest.subscribeDesc');

    const btns = [actionButton, cancelBtnProps];

    return (
        <>
            <Modal
                state="default"
                open={isDigestOpen}
                position="center"
                onCloseHandler={() => setisDigestOpen(false)}
                title={title}
                supportingText={description}
                btnsProps={btns}
            />
        </>
    );
}

